import { useState, useEffect, useCallback } from 'react'
import { startOfISOWeek, addDays, addWeeks, subWeeks, format, isToday } from 'date-fns'
import { leaveApi, LeaveType, DriverLeaveRow, DriverLeaveEntry } from '@/api/leaves'

const WEEKDAY = ['一', '二', '三', '四', '五', '六', '日']

export default function LeaveCalendar() {
  const [weekStart, setWeekStart] = useState<Date>(() => startOfISOWeek(new Date()))
  const [rows, setRows] = useState<DriverLeaveRow[]>([])
  const [leaveTypes, setLeaveTypes] = useState<LeaveType[]>([])
  const [loading, setLoading] = useState(true)
  const [popover, setPopover] = useState<{ driverId: string; date: string } | null>(null)
  const [saving, setSaving] = useState(false)

  const weekEnd = addDays(weekStart, 6)
  const fromStr = format(weekStart, 'yyyy-MM-dd')
  const toStr = format(weekEnd, 'yyyy-MM-dd')

  const loadLeaveTypes = useCallback(async () => {
    try {
      const types = await leaveApi.listLeaveTypes()
      setLeaveTypes(types)
    } catch {
      // ignore
    }
  }, [])

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const data = await leaveApi.listWeekly(fromStr, toStr)
      setRows(data)
    } finally {
      setLoading(false)
    }
  }, [fromStr])

  useEffect(() => { loadLeaveTypes() }, [loadLeaveTypes])
  useEffect(() => { load() }, [load])

  const leaveMap: Record<string, DriverLeaveEntry> = {}
  for (const row of rows) {
    for (const leave of row.leaves) {
      leaveMap[`${row.driver_id}__${leave.date}`] = leave
    }
  }

  const days = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i))

  const handleCellClick = async (driverId: string, dateStr: string) => {
    const existing = leaveMap[`${driverId}__${dateStr}`]
    if (existing) {
      setSaving(true)
      try {
        await leaveApi.deleteLeave(existing.id)
        await load()
      } finally {
        setSaving(false)
      }
    } else {
      setPopover({ driverId, date: dateStr })
    }
  }

  const handleAddLeave = async (leaveTypeId: string) => {
    if (!popover) return
    setSaving(true)
    setPopover(null)
    try {
      await leaveApi.createLeave(popover.driverId, {
        date: popover.date,
        leave_type_id: leaveTypeId,
      })
      await load()
    } catch (e: any) {
      alert('建立失敗：' + (e.response?.data?.detail ?? e.message))
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="relative">
      {/* Week navigation */}
      <div className="flex items-center gap-3 mb-4">
        <button
          onClick={() => setWeekStart((w) => subWeeks(w, 1))}
          className="p-1.5 rounded-lg border hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300 cursor-pointer"
        >
          ‹
        </button>
        <span className="text-sm font-medium text-gray-700 dark:text-gray-300 min-w-[160px] text-center">
          {format(weekStart, 'yyyy/MM/dd')} – {format(weekEnd, 'MM/dd')}
        </span>
        <button
          onClick={() => setWeekStart((w) => addWeeks(w, 1))}
          className="p-1.5 rounded-lg border hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300 cursor-pointer"
        >
          ›
        </button>
        <button
          onClick={() => setWeekStart(startOfISOWeek(new Date()))}
          className="text-xs text-blue-600 hover:underline ml-1 cursor-pointer"
        >
          本週
        </button>
        {saving && (
          <span className="text-xs text-blue-500 animate-pulse ml-2">儲存中…</span>
        )}
      </div>

      {/* Legend */}
      {leaveTypes.length > 0 && (
        <div className="flex items-center gap-3 mb-3 text-xs text-gray-500 flex-wrap">
          <span>點格子新增假期，再點一次取消。</span>
          {leaveTypes.map((lt) => (
            <span key={lt.id} className="inline-flex items-center gap-1">
              <span className="w-3 h-3 rounded-sm inline-block" style={{ backgroundColor: lt.color }} />
              {lt.name}
            </span>
          ))}
        </div>
      )}

      {/* Table */}
      {loading ? (
        <div className="text-center text-gray-400 py-12">載入中…</div>
      ) : rows.length === 0 ? (
        <div className="text-center text-gray-400 py-12">尚無司機資料</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr>
                <th className="text-left px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 w-28 text-gray-600 dark:text-gray-400 font-medium">
                  司機
                </th>
                {days.map((d, i) => (
                  <th
                    key={i}
                    className={`px-2 py-2 border border-gray-200 dark:border-gray-700 text-center font-medium text-xs w-24 ${
                      isToday(d)
                        ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400'
                        : 'bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-400'
                    }`}
                  >
                    <div>{WEEKDAY[i]}</div>
                    <div className="font-normal text-gray-400 dark:text-gray-500">{format(d, 'M/d')}</div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.driver_id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                  <td className="px-3 py-2 border border-gray-200 dark:border-gray-700 font-medium text-gray-800 dark:text-gray-200 truncate max-w-[112px]">
                    {row.driver_name}
                  </td>
                  {days.map((d, i) => {
                    const dateStr = format(d, 'yyyy-MM-dd')
                    const leave = leaveMap[`${row.driver_id}__${dateStr}`]
                    return (
                      <td
                        key={i}
                        onClick={() => handleCellClick(row.driver_id, dateStr)}
                        className={`border border-gray-200 dark:border-gray-700 text-center cursor-pointer transition-colors select-none ${
                          isToday(d) ? 'bg-blue-50 dark:bg-blue-900/20' : ''
                        } ${!leave ? 'hover:bg-gray-100 dark:hover:bg-gray-700' : ''}`}
                      >
                        {leave ? (
                          <div
                            className="mx-1 my-1 rounded text-xs py-1 font-medium text-white"
                            style={{ backgroundColor: leave.leave_type_color }}
                          >
                            {leave.leave_type_name}
                          </div>
                        ) : (
                          <div className="h-7" />
                        )}
                      </td>
                    )
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Popover: choose leave type */}
      {popover && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setPopover(null)} />
          <div
            className="fixed z-50 bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 p-4 w-52"
            style={{ top: '50%', left: '50%', transform: 'translate(-50%,-50%)' }}
          >
            <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
              {popover.date} 請假類型
            </p>
            {leaveTypes.length === 0 ? (
              <p className="text-xs text-gray-400 text-center py-2">
                請先在「系統設定 → 假別管理」新增假別
              </p>
            ) : (
              <div className="space-y-2">
                {leaveTypes.map((lt) => (
                  <button
                    key={lt.id}
                    onClick={() => handleAddLeave(lt.id)}
                    className="w-full py-2 rounded-lg text-white text-sm font-medium hover:opacity-90 transition-opacity cursor-pointer"
                    style={{ backgroundColor: lt.color }}
                  >
                    {lt.name}
                  </button>
                ))}
              </div>
            )}
            <button
              onClick={() => setPopover(null)}
              className="mt-2 w-full py-1.5 rounded-lg border border-gray-200 dark:border-gray-600 text-sm text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
            >
              取消
            </button>
          </div>
        </>
      )}
    </div>
  )
}
